import { z } from 'zod';

// ============================================
// 故事历史类型
// ============================================

/**
 * 故事历史条目的显示角色。
 * 这里保留 narrator 作为局部叙事者，避免继续沿用 system 作为展示语义。
 */
export const StoryHistoryEntryRole = z.enum(['user', 'assistant', 'narrator', 'system']);
export type StoryHistoryEntryRoleValue = z.infer<typeof StoryHistoryEntryRole>;

/**
 * 故事历史条目的来源类型。
 * 用于标记该条内容是从哪个分支系统写入的。
 */
export const StoryHistorySourceType = z.enum([
  'system',
  'conversation',
  'scene',
  'event',
  'quest',
  'combat',
  'exploration',
  'world_book',
]);
export type StoryHistorySourceTypeValue = z.infer<typeof StoryHistorySourceType>;

/**
 * 故事历史条目的基础结构。
 * 这是后续主历史 store、局部窗口和 prompt 抽取器共享的核心数据形状。
 */
export const StoryHistoryEntrySchema = z.object({
  id: z.string(),
  timestamp: z.number(),
  gameTime: z.string().optional(),
  role: StoryHistoryEntryRole,
  sourceType: StoryHistorySourceType,
  sourceId: z.string().optional(),
  sessionKey: z.string().optional(),
  sceneName: z.string().optional(),
  participantIds: z.array(z.string()).default([]),
  content: z.string(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});
export type StoryHistoryEntry = z.infer<typeof StoryHistoryEntrySchema>;

/**
 * 写入故事历史时的输入结构。
 * id 和 timestamp 由底层服务统一补齐，因此在调用层可以不传。
 */
export const StoryHistoryEntryInputSchema = StoryHistoryEntrySchema.omit({
  id: true,
  timestamp: true,
}).extend({
  id: z.string().optional(),
  timestamp: z.number().optional(),
});
export type StoryHistoryEntryInput = z.infer<typeof StoryHistoryEntryInputSchema>;

/**
 * 故事历史查询条件。
 * 供主历史面板、窗口筛选器、prompt 抽取器复用。
 */
export const StoryHistoryQuerySchema = z.object({
  sessionKey: z.string().optional(),
  sceneName: z.string().optional(),
  sourceType: StoryHistorySourceType.optional(),
  role: StoryHistoryEntryRole.optional(),
  participantId: z.string().optional(),
  keyword: z.string().optional(),
  sinceTimestamp: z.number().optional(),
  untilTimestamp: z.number().optional(),
  limit: z.number().int().positive().optional(),
});
export type StoryHistoryQuery = z.infer<typeof StoryHistoryQuerySchema>;

/**
 * 故事历史的整体状态。
 * 先保留条目数组和容量上限，后续可再扩展索引和摘要层。
 */
export const StoryHistoryStateSchema = z.object({
  entries: z.array(StoryHistoryEntrySchema),
  maxEntries: z.number().int().positive().default(3000),
});
export type StoryHistoryState = z.infer<typeof StoryHistoryStateSchema>;
